"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Monitor, Tablet, Smartphone, Globe, Tv, ChefHat } from "lucide-react";

const NODES = [
  { icon: Tv, label: { en: "Menu board TV", es: "TV menu board" } },
  { icon: Smartphone, label: { en: "Mobile site", es: "Sitio móvil" } },
  { icon: Tablet, label: { en: "Self-order kiosk", es: "Kiosko" } },
  { icon: Globe, label: { en: "Website", es: "Sitio web" } },
  { icon: Monitor, label: { en: "Customer display", es: "Customer display" } },
  { icon: ChefHat, label: { en: "KDS · prep cards", es: "KDS · prep cards" } },
];

export default function MenuBoardsRadialScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("One source. Every surface.", "Un source. Cada superficie.")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Six screens. ", "Seis pantallas. ")}
            <span className="text-redy-charcoal/30">{t("One menu.", "Un solo menú.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Your menu lives in one place — the POS. From there it radiates to the menu board TVs, the kiosk, the customer display, the website, the mobile site, and even prep cards on the KDS. Edit once. Live everywhere.",
              "Tu menú vive en un solo lugar — el POS. Desde ahí irradia a las TVs de menu board, el kiosko, el customer display, el sitio web, el sitio móvil y hasta las prep cards del KDS. Editas una vez. Vivo en todos lados."
            )}
          </p>
        </motion.div>

        {/* Radial diagram */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative mx-auto"
          style={{ width: "100%", maxWidth: 640, aspectRatio: "1" }}
        >
          {/* Pulsing rings */}
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.8 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
              style={{
                width: 120,
                height: 120,
                border: "1px solid rgba(244,114,43,0.30)",
              }}
            />
          ))}

          {/* Central POS node */}
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full flex flex-col items-center justify-center text-white shadow-2xl"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244,114,43,0.40)",
            }}
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-white/85">POS</div>
            <div className="text-[14px] font-black">Redy</div>
            <div className="text-[8px] font-bold uppercase tracking-wider text-white/75 mt-0.5">
              {t("Source", "Source")}
            </div>
          </motion.div>

          {/* 6 nodes around the perimeter */}
          {NODES.map((node, i) => {
            const angle = (i / NODES.length) * 2 * Math.PI - Math.PI / 2;
            const radius = 42; // % of container
            const x = 50 + radius * Math.cos(angle);
            const y = 50 + radius * Math.sin(angle);
            const Icon = node.icon;
            return (
              <motion.div
                key={node.label.en}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.5,
                  delay: 0.6 + 0.12 * i,
                  ease: [0.21, 0.47, 0.32, 0.98],
                }}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <div className="w-14 h-14 rounded-full bg-white border border-black/10 flex items-center justify-center shadow-md">
                  <Icon className="w-5 h-5" style={{ color: "#F4722B" }} />
                </div>
                <div className="text-[10px] font-bold text-redy-charcoal text-center whitespace-nowrap">
                  {t(node.label.en, node.label.es)}
                </div>
              </motion.div>
            );
          })}

          {/* Connecting lines (SVG) */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {NODES.map((_, i) => {
              const angle = (i / NODES.length) * 2 * Math.PI - Math.PI / 2;
              const radius = 42;
              const x = 50 + radius * Math.cos(angle);
              const y = 50 + radius * Math.sin(angle);
              return (
                <motion.line
                  key={i}
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.25 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.5 + 0.1 * i }}
                  x1="50"
                  y1="50"
                  x2={x}
                  y2={y}
                  stroke="#F4722B"
                  strokeWidth="0.3"
                  strokeDasharray="1 1"
                />
              );
            })}
          </svg>
        </motion.div>

        {/* Caption */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 1.5 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#FAFAF8] px-4 py-2.5 rounded-full border border-black/5">
            <div
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: "#4A7AE8" }}
            />
            <span className="text-[12px] font-bold text-redy-charcoal">
              {t("All screens in sync.", "Todas las pantallas en sync.")}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
